/**
 * Feature E02-F05 — Kick-off questionnaire FRONT-END model (pure, DOM-free).
 *
 * 1:1 port of the non-render logic of `../EER app/public/assets/js/project-kickoff.js`:
 *   - buildMergedKickoffQuestions (T05) — `_buildMergedKickoffQuestions`
 *   - detectMissing / answeredCounts (T07/T03)
 *   - groupByCategory + CATEGORY_ICONS (T03 — renderStage3Content grouping)
 *   - bullet-point keyboard helper (T03 — `_handleBulletKey`: Enter continues a
 *     bullet, empty bullet is removed, Ctrl+Shift+L toggles a bullet prefix)
 *   - isSalaryQuestion (T03 — `q.text.toLowerCase().includes('market salary')`)
 *   - buildKickoffSaveUpdates (T06 — the update_project bag)
 *
 * Kept framework-free so the React component (kickoffView.tsx) and the unit tests
 * (T03..T07) both consume the SAME rules — no logic in the JSX. Pure functions
 * mirror the legacy helpers verbatim; the SQL/DB-side equivalents live in
 * `packages/api/src/services/kickoffQuestionnaire.ts` (same merge contract).
 */
import {
  KICKOFF_CATEGORY_ORDER,
  type KickoffQuestionItem,
  type PositionTypeQuestion,
  type KickoffSaveUpdates,
} from '@eer/shared';

export const POSITION_SPECIFIC_CATEGORY = 'Position-Specific' as const;

// ---------------------------------------------------------------------------
// T03 — category icons + grouping (renderStage3Content)
// ---------------------------------------------------------------------------

/** FontAwesome icon class per category (legacy `categoryIcons`), 'Other' fallback. */
export const CATEGORY_ICONS: Record<string, string> = {
  'For Sourcing Use': 'fa-magnifying-glass text-purple-500',
  'Getting to Know the Client': 'fa-building text-blue-500',
  'The Ideal Candidate': 'fa-user-check text-amber-500',
  'About the Position': 'fa-briefcase text-green-500',
  'Building the Need': 'fa-handshake text-rose-500',
  'Position-Specific': 'fa-star text-orange-500',
};

/** Resolve the icon for a category (default `fa-circle text-gray-400`). */
export function categoryIcon(category: string): string {
  return CATEGORY_ICONS[category] ?? 'fa-circle text-gray-400';
}

/** True for the salary question that renders the market-rate search button. */
export function isSalaryQuestion(q: { text?: string }): boolean {
  return (q.text ?? '').toLowerCase().includes('market salary');
}

/** A question carrying its index in the flat list (so edits map back). */
export interface IndexedQuestion extends KickoffQuestionItem {
  idx: number;
}

/** One rendered category group: its name, items (with flat index), counters. */
export interface CategoryGroup {
  category: string;
  items: IndexedQuestion[];
  answered: number;
  total: number;
}

/**
 * Group the flat question list by category, preserving first-seen order
 * (`renderStage3Content` category grouping). Each item keeps its flat index so
 * answer/text edits update the right slot. Per-category answered/total counts.
 */
export function groupByCategory(questions: readonly KickoffQuestionItem[]): CategoryGroup[] {
  const order: string[] = [];
  const map = new Map<string, IndexedQuestion[]>();
  questions.forEach((q, idx) => {
    const cat = q.category || 'Other';
    if (!map.has(cat)) {
      map.set(cat, []);
      order.push(cat);
    }
    map.get(cat)!.push({ ...q, idx });
  });
  return order.map((category) => {
    const items = map.get(category)!;
    return {
      category,
      items,
      answered: items.filter(isAnswered).length,
      total: items.length,
    };
  });
}

// ---------------------------------------------------------------------------
// T05 — merge base template + position-type questions + saved answers
// (mirror of the backend service so the front and back share one contract)
// ---------------------------------------------------------------------------

/** Coerce `position_types.questions` (object/array/JSON-string) → typed array. */
export function coercePositionTypeQuestions(raw: unknown): PositionTypeQuestion[] {
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((q): q is { text: unknown; category?: unknown } => !!q && typeof q === 'object')
    .filter((q) => typeof q.text === 'string' && q.text.length > 0)
    .map((q) => ({
      text: String(q.text),
      category:
        typeof q.category === 'string' && q.category.length > 0
          ? q.category
          : POSITION_SPECIFIC_CATEGORY,
    }));
}

export interface MergeInput {
  template: readonly KickoffQuestionItem[];
  positionTypeQuestions?: unknown;
  savedQuestions?: readonly { category?: string; text?: string; answer?: string; done?: boolean }[];
}

/**
 * `_buildMergedKickoffQuestions`: base template → +position-type (dedup by
 * text+category) → carry saved answers (text-match) → legacy saved appended last.
 * `data.kickoff_questions` is the ANSWER source, never the canonical list.
 */
export function buildMergedKickoffQuestions(input: MergeInput): KickoffQuestionItem[] {
  const base: KickoffQuestionItem[] = (input.template ?? []).map((q) => ({
    category: q.category,
    text: q.text,
    answer: '',
    done: false,
  }));

  for (const pq of coercePositionTypeQuestions(input.positionTypeQuestions)) {
    const cat = pq.category || POSITION_SPECIFIC_CATEGORY;
    const exists = base.some((q) => q.text === pq.text && q.category === cat);
    if (!exists) base.push({ category: cat, text: pq.text, answer: '', done: false });
  }

  const savedByText = new Map<string, { answer?: string; done?: boolean; category?: string }>();
  for (const s of input.savedQuestions ?? []) {
    if (s && typeof s.text === 'string' && s.text.length > 0) savedByText.set(s.text, s);
  }
  for (const q of base) {
    const s = savedByText.get(q.text);
    if (s) {
      q.answer = s.answer ?? '';
      q.done = !!s.done;
      savedByText.delete(q.text);
    }
  }
  for (const [text, s] of savedByText) {
    base.push({ category: s.category || 'Other', text, answer: s.answer ?? '', done: !!s.done });
  }

  return base;
}

// ---------------------------------------------------------------------------
// T07 — missing-data detection + T03 counters
// ---------------------------------------------------------------------------

/** Answered ⇔ `(answer||'').trim()` is non-empty. */
export function isAnswered(q: { answer?: string }): boolean {
  return (q.answer ?? '').trim().length > 0;
}

export interface MissingData {
  unanswered: KickoffQuestionItem[];
  count: number;
  short: KickoffQuestionItem[];
  hasMore: boolean;
}

/** Unanswered slice + first-5 collapsed view (`renderStage3Content` missing block). */
export function detectMissing(questions: readonly KickoffQuestionItem[]): MissingData {
  const unanswered = questions.filter((q) => !isAnswered(q));
  return {
    unanswered,
    count: unanswered.length,
    short: unanswered.slice(0, 5),
    hasMore: unanswered.length > 5,
  };
}

/** answered / total for the header counter. */
export function answeredCounts(questions: readonly KickoffQuestionItem[]): {
  answered: number;
  total: number;
} {
  return { answered: questions.filter(isAnswered).length, total: questions.length };
}

// ---------------------------------------------------------------------------
// T03 — question list mutations (add / remove / edit-text / set-answer)
// ---------------------------------------------------------------------------

/** Set the answer at `idx` (immutable). Out-of-range → unchanged. */
export function setAnswer(
  questions: readonly KickoffQuestionItem[],
  idx: number,
  answer: string,
): KickoffQuestionItem[] {
  if (idx < 0 || idx >= questions.length) return [...questions];
  return questions.map((q, i) => (i === idx ? { ...q, answer } : q));
}

/** Edit the question text at `idx` (immutable). Out-of-range → unchanged. */
export function setQuestionText(
  questions: readonly KickoffQuestionItem[],
  idx: number,
  text: string,
): KickoffQuestionItem[] {
  if (idx < 0 || idx >= questions.length) return [...questions];
  return questions.map((q, i) => (i === idx ? { ...q, text } : q));
}

/**
 * Add a blank question to a category (`addKickoffQuestion`): inserted right after
 * the LAST existing question of that category, else appended. Returns the new
 * list AND the index of the inserted row (so the UI can focus it for editing).
 */
export function addQuestion(
  questions: readonly KickoffQuestionItem[],
  category: string,
): { questions: KickoffQuestionItem[]; insertedIdx: number } {
  const list = [...questions];
  let lastIdx = -1;
  list.forEach((q, i) => {
    if (q.category === category) lastIdx = i;
  });
  const newQ: KickoffQuestionItem = {
    category,
    text: 'Additional question',
    answer: '',
    done: false,
  };
  if (lastIdx >= 0) {
    list.splice(lastIdx + 1, 0, newQ);
    return { questions: list, insertedIdx: lastIdx + 1 };
  }
  list.push(newQ);
  return { questions: list, insertedIdx: list.length - 1 };
}

// ---------------------------------------------------------------------------
// E12-F08-T15 — re-hydration from a server refetch without losing local typing
// ---------------------------------------------------------------------------

/** Field-wise equality of two questionnaire rows. */
export function sameQuestion(
  a: KickoffQuestionItem | undefined,
  b: KickoffQuestionItem | undefined,
): boolean {
  if (!a || !b) return a === b;
  return (
    a.category === b.category &&
    a.text === b.text &&
    (a.answer ?? '') === (b.answer ?? '') &&
    Boolean(a.done) === Boolean(b.done)
  );
}

/** Result of `rehydrateQuestions`: the list to show + the new "last sent" baseline. */
export interface RehydrateResult {
  readonly questions: KickoffQuestionItem[];
  readonly base: KickoffQuestionItem[];
}

/**
 * E12-F08-T15 — merge a refetched server questionnaire into the local one.
 *
 * `base` is the local list as of the last SENT save (or the last hydration). A
 * row that differs locally from `base` is a local edit not yet confirmed by the
 * server — it wins over the server copy (it goes out with the next autosave). A
 * row without local edits takes the server copy, so external updates (AI
 * Pre-fill / AI Processing) still arrive.
 *
 * Row-wise merge needs aligned lists. If the local STRUCTURE changed since `base`
 * (a question added/removed and not yet saved), local wins wholesale; if there are
 * no local edits at all, the server copy wins wholesale.
 *
 * The returned `base` keeps the old baseline for rows still dirty (so they stay
 * dirty against a later refetch) and adopts the server row for the rest.
 */
export function rehydrateQuestions(
  local: readonly KickoffQuestionItem[],
  base: readonly KickoffQuestionItem[],
  server: readonly KickoffQuestionItem[],
): RehydrateResult {
  const structureChanged = local.length !== base.length;
  const dirty = structureChanged ? [] : local.map((q, i) => !sameQuestion(q, base[i]));
  const anyDirty = structureChanged || dirty.some(Boolean);
  if (!anyDirty) return { questions: [...server], base: [...server] };
  if (structureChanged || server.length !== local.length) {
    return { questions: [...local], base: [...base] };
  }
  return {
    questions: local.map((q, i) => (dirty[i] ? q : server[i]!)),
    base: base.map((b, i) => (dirty[i] ? b : server[i]!)),
  };
}

/** Remove the question at `idx` (`removeKickoffQuestion`). Out-of-range → unchanged. */
export function removeQuestion(
  questions: readonly KickoffQuestionItem[],
  idx: number,
): KickoffQuestionItem[] {
  if (idx < 0 || idx >= questions.length) return [...questions];
  return questions.filter((_, i) => i !== idx);
}

// ---------------------------------------------------------------------------
// T03 — bullet-point keyboard helper (`_handleBulletKey`)
// ---------------------------------------------------------------------------

/** Result of a bullet-key transform: the next value + the next caret position. */
export interface BulletEdit {
  value: string;
  caret: number;
}

/**
 * Apply the bullet-point rule to a textarea value on a key event
 * (`_handleBulletKey`):
 *   - Enter on a `- `/`• ` line continues the bullet on the next line;
 *   - Enter on an EMPTY bullet line removes the bullet (no new line);
 *   - Ctrl+Shift+L toggles a `- ` prefix on the current line.
 * Returns the new {value, caret}, or `null` when the key does not apply (the
 * caller lets the default keypress through). Pure — no DOM.
 */
export function applyBulletKey(
  key: string,
  mods: { shift: boolean; ctrl: boolean },
  text: string,
  caret: number,
): BulletEdit | null {
  // Enter (no shift / no ctrl): continue or clear a bullet.
  if (key === 'Enter' && !mods.shift && !mods.ctrl) {
    const lineStart = text.lastIndexOf('\n', caret - 1) + 1;
    const line = text.substring(lineStart, caret);
    const bulletMatch = line.match(/^(\s*[-•]\s)/);
    if (!bulletMatch) return null;
    // Empty bullet → remove it instead of adding a new one.
    if (line.trim() === '-' || line.trim() === '•') {
      return {
        value: text.substring(0, lineStart) + text.substring(caret),
        caret: lineStart,
      };
    }
    const insert = '\n' + bulletMatch[1];
    return {
      value: text.substring(0, caret) + insert + text.substring(caret),
      caret: caret + insert.length,
    };
  }
  // Ctrl+Shift+L: toggle a `- ` prefix on the current line.
  if ((key === 'l' || key === 'L') && mods.ctrl && mods.shift) {
    const lineStart = text.lastIndexOf('\n', caret - 1) + 1;
    if (text.substring(lineStart, lineStart + 2) === '- ') {
      return {
        value: text.substring(0, lineStart) + text.substring(lineStart + 2),
        caret: Math.max(caret - 2, lineStart),
      };
    }
    return {
      value: text.substring(0, lineStart) + '- ' + text.substring(lineStart),
      caret: caret + 2,
    };
  }
  return null;
}

/** Auto-resize height for a textarea given its scrollHeight (min 28px). */
export function autoResizeHeight(scrollHeight: number): number {
  return Math.max(scrollHeight, 28);
}

// ---------------------------------------------------------------------------
// T04 — view-mode toggle
// ---------------------------------------------------------------------------

export type KickoffViewMode = 'questions' | 'notes';

/** `_toggleKickoffView`: flip between Mode A (questions) and Mode B (notes). */
export function toggleViewMode(mode: KickoffViewMode): KickoffViewMode {
  return mode === 'questions' ? 'notes' : 'questions';
}

// ---------------------------------------------------------------------------
// T06 — build the update_project save bag
// ---------------------------------------------------------------------------

export interface KickoffSaveInput {
  /** Omit (undefined) to leave the saved notes untouched server-side (E02-F05-T09). */
  notes?: string;
  /** Omit (undefined) to leave the saved transcript untouched server-side (E02-F05-T09). */
  transcript?: string;
  questions: readonly KickoffQuestionItem[];
  meetingCompleted?: boolean;
}

/**
 * The kick-off save bag the SPA actually sends. Unlike the shared
 * `KickoffSaveUpdates` (where notes/transcript default to ''), here they are
 * OPTIONAL: E02-F05-T09 — a field the user did not edit is simply absent, so the
 * server's merge keeps the saved text instead of overwriting it with ''.
 */
export type KickoffSavePatch = Omit<KickoffSaveUpdates, 'kickoff_notes' | 'kickoff_transcript'> & {
  kickoff_notes?: string;
  kickoff_transcript?: string;
};

/**
 * Build the `updates` bag (`saveKickoffData` / `completeMeeting` /
 * `_doAutoSaveKickoff`): trimmed notes/transcript (only those passed) + the full
 * question list, with `meeting_completed:true` ONLY for Complete Meeting. Merges
 * into projects.data server-side (never a whole-data overwrite).
 */
export function buildKickoffSaveUpdates(input: KickoffSaveInput): KickoffSavePatch {
  const bag: KickoffSavePatch = { kickoff_questions: [...input.questions] };
  if (input.notes !== undefined) bag.kickoff_notes = input.notes.trim();
  if (input.transcript !== undefined) bag.kickoff_transcript = input.transcript.trim();
  if (input.meetingCompleted === true) bag.meeting_completed = true;
  return bag;
}

/**
 * E02-F05-T09 — read the saved kick-off notes/transcript from `projects.data`
 * (canonical snake_case keys, legacy camelCase fallback — see the api's
 * kickoffDataKeys.ts). This is what seeds the editors when the step mounts;
 * before T09 nothing passed it, so the editors always opened empty.
 */
export function readSavedKickoffText(data: unknown): { notes: string; transcript: string } {
  const d = data !== null && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const pick = (key: string, legacyKey: string): string => {
    const v = d[key];
    if (typeof v === 'string' && v !== '') return v;
    const legacy = d[legacyKey];
    return typeof legacy === 'string' ? legacy : '';
  };
  return {
    notes: pick('kickoff_notes', 'kickoffNotes'),
    transcript: pick('kickoff_transcript', 'kickoffTranscript'),
  };
}

/** Category display order for any UI that needs a stable sort (re-export). */
export { KICKOFF_CATEGORY_ORDER };
